import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Crown, Users, Target, Rocket, Gift } from "lucide-react";
import { BOARD } from "../lib/content";
import { iconOf } from "../lib/icons";
import { fileUrl } from "../lib/api";


function Block({ icon: Icon, title, text, tid }) {
  return (
    <div data-testid={tid} className="flex gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#7A2E2A]/30 border border-[#F0E8DB]/40 flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[#F0E8DB]" />
      </div>
      <div>
        <div className="text-[#F0E8DB] text-xs mb-1">{title}</div>
        <p className="text-[#C5B8A8] text-sm leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function BoardCard({ members, content }) {
  const meta = content.committees[BOARD] || {};
  const [president, vice, ...rest] = members;
  const Person = ({ m, big }) => (
    <div data-testid={`team-member-${m.id}`} className={`flex flex-col items-center text-center ${big ? "" : ""}`}>
      <div className={`${big ? "w-28 h-28 text-4xl" : "w-20 h-20 text-2xl"} rounded-full border-2 ${big ? "border-[#F0E8DB] gold-glow" : "border-[#F0E8DB]/50"} bg-gradient-to-br from-[#7A2E2A] to-[#3d1614] flex items-center justify-center font-bold text-[#F0E8DB] mb-3`}>
        {(m.name || "؟")[0]}
      </div>
      <div className="text-[#F0E8DB] text-xs mb-1">{m.role}</div>
      <div className={`text-[#F0E8DB] font-semibold ${big ? "text-xl" : "text-base"}`}>{m.name}</div>
    </div>
  );
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      data-testid="committee-card-مجلس الإدارة"
      className="relative rounded-3xl overflow-hidden border border-[#F0E8DB]/50 bg-[#1f1110]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(240,232,219,0.22),transparent_55%)]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F0E8DB] to-transparent" />
      <div className="relative p-8 sm:p-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F0E8DB]/50 bg-[#7A2E2A]/30 text-[#F0E8DB] text-xs tracking-widest">
            <Crown size={14} /> {meta.tagline}
          </div>
          <h3 className="text-[#F0E8DB] font-bold text-3xl sm:text-4xl mt-4">{content.titles.board}</h3>
          <p className="text-[#A89A88] text-sm sm:text-base mt-3 max-w-2xl mx-auto leading-relaxed">{meta.does}</p>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mb-10">
          {president && <div className="md:order-2"><Person m={president} big /></div>}
          {vice && <div className="md:order-1"><Person m={vice} /></div>}
        </div>

        {rest.length > 0 && (
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {rest.map((m) => (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="rounded-2xl border border-[#F0E8DB]/20 bg-[#241615]/70 backdrop-blur px-4 py-4 text-center w-full sm:w-[calc(33.333%-0.7rem)]">
                <div className="text-[#F0E8DB] text-xs mb-1 leading-snug">{m.role}</div>
                <div className="text-[#F0E8DB] font-semibold">{m.name}</div>
              </div>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mt-10 pt-8 border-t border-[#F0E8DB]/20">
          <Block icon={Rocket} title="طموحنا" text={meta.ambition} tid="committee-ambition-مجلس الإدارة" />
          <Block icon={Gift} title="ماذا تستفيد كطالب" text={meta.gain} tid="committee-gain-مجلس الإدارة" />
        </div>
      </div>
    </motion.article>
  );
}

function CommitteeCard({ title, members, idx, content, def }) {
  if (title === BOARD) return <BoardCard members={members} content={content} />;
  const meta = content.committees[title] || {};
  const Icon = def ? iconOf(def.icon) : Users;
  const image = def?.image ? fileUrl(def.image) : "";
  const flip = idx % 2 === 1;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      data-testid={`committee-card-${title}`}
      className={`theater-card rounded-3xl overflow-hidden grid lg:grid-cols-5 ${flip ? "lg:[direction:ltr]" : ""}`}
    >
      <div className="relative lg:col-span-2 min-h-[220px] lg:min-h-full [direction:rtl]">
        {image && <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover opacity-70" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0F0E] via-[#1A0F0E]/40 to-transparent lg:bg-gradient-to-l" />
        <div className="absolute bottom-5 right-5 left-5">
          <div className="w-12 h-12 rounded-xl bg-[#7A2E2A]/70 backdrop-blur border border-[#F0E8DB]/60 flex items-center justify-center mb-3">
            <Icon size={22} className="text-[#F0E8DB]" />
          </div>
          <h3 className="text-[#F0E8DB] font-bold text-xl sm:text-2xl leading-tight">{title}</h3>
          {meta.tagline && <div className="text-[#F0E8DB] text-sm mt-1">{meta.tagline}</div>}
        </div>
      </div>

      <div className="lg:col-span-3 p-6 sm:p-8 space-y-5 [direction:rtl]">
        <Block icon={Target} title="ماذا نفعل" text={meta.does} tid={`committee-does-${title}`} />
        <Block icon={Rocket} title="طموحنا" text={meta.ambition} tid={`committee-ambition-${title}`} />
        <Block icon={Gift} title="ماذا تستفيد كطالب" text={meta.gain} tid={`committee-gain-${title}`} />
        <div className="pt-4 border-t border-[#F0E8DB]/20">
          <div className="text-[#F0E8DB] text-xs mb-3">الفريق</div>
          <ul className="flex flex-wrap gap-2">
            {members.map((m) => (
              <li key={m.id} data-testid={`team-member-${m.id}`} className="inline-flex items-baseline gap-2 px-3 py-1.5 rounded-full bg-[#241615] border border-[#F0E8DB]/20">
                <span className="text-[#F0E8DB] text-xs">{m.role}</span>
                <span className="text-[#F0E8DB] text-sm font-semibold">{m.name}</span>
              </li>
            ))}
            {members.length === 0 && <li className="text-[#BFB3A0] text-sm">المقاعد شاغرة — قد تكون أنت!</li>}
          </ul>
        </div>
      </div>
    </motion.article>
  );
}

export default function TeamSection({ team, content }) {
  const groups = useMemo(() => {
    const map = new Map();
    team.forEach((m) => {
      const key = m.committee || "أخرى";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    });
    const ORDER = [BOARD, ...content.committee_list.map((c) => c.name)];
    ORDER.forEach((k) => { if (!map.has(k)) map.set(k, []); });
    const keys = Array.from(map.keys()).sort((a, b) => {
      const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return keys.map((k) => [k, map.get(k).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))]);
  }, [team, content]);

  return (
    <section id="team" data-testid="team-section" className="py-20 md:py-28 bg-[#1A0F0E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-14">
          <div className="text-[#F0E8DB] text-xs tracking-[0.4em] font-serif-en mb-3">COMMITTEES</div>
          <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl">{content.titles.team}</h2>
          {content.team_intro && <p className="text-[#A89A88] mt-4 max-w-2xl mx-auto">{content.team_intro}</p>}
        </div>
        <div className="space-y-8">
          {groups.map(([title, members], i) => <CommitteeCard key={title} title={title} members={members} idx={i} content={content} def={content.committee_list.find((c) => c.name === title)} />)}
        </div>
      </div>
    </section>
  );
}
